import { useMemo } from 'react';

import { OrderDirection } from 'src/hooks/useSortAndOrder';
import type { SortParameters } from 'src/types';

/**
 * An utility function to compare two unknown values for sorting purposes.
 * @param valueA - Value to compare.
 * @param valueB - Value to compare.
 * @returns -1/0/1
 */
export const compareTwoValues = (valueA: unknown, valueB: unknown): number => {
  let a = valueA;
  let b = valueB;
  // For two strings we want to convert them to lowercase - so 'test' will be alongside 'Test'
  if (typeof a === 'string' && typeof b === 'string') {
    a = a.toLowerCase();
    b = b.toLowerCase();
  }
  // For undefined values we use empty strings - so they will be at the top/bottom
  if (a === undefined) {
    a = '';
  }
  if (b === undefined) {
    b = '';
  }
  // @ts-expect-error We are operating unknown values at this point, comparing them is ok for JS.
  if (a < b) {
    return -1;
  }
  // @ts-expect-error We are operating unknown values at this point, comparing them is ok for JS.
  if (a > b) {
    return 1;
  }
  return 0;
};

const toSortableNumber = (value: unknown): number => {
  const num = Number(value);
  return Number.isNaN(num) ? Infinity : num;
};

// Default sorting function used by useSortedRows, just sort by field values
export const compareRowsByFieldName = <DTO extends Record<string, unknown>>(rowA: DTO, rowB: DTO, fieldName: string): number =>
  compareTwoValues(rowA[fieldName], rowB[fieldName]);

type UseSortedRowsParams<DTO extends Record<string, unknown>, F extends string> = {
  rows: DTO[];
  sortParameters: SortParameters;
} & (
  | { sortFunction?: (rowA: DTO, rowB: DTO, fieldName: F) => number; numericFields?: never }
  | { numericFields: readonly string[]; sortFunction?: never }
);

/**
 * A hook to sort any kind of rows on front-end, uses same api the back-end does - to be used in conjunction with
 * useSortAndOrder or useSortAndOrderState.
 *
 * compareRowsByFieldName and compareTwoValues are also exported to be used in custom sorting functions
 * (see PromotionAccounts for example).
 *
 * @param params.rows - Array of DTO to sort.
 * @param params.sortParameters - Parameters for sort function (sort field and order direction).
 * @param params.sortFunction - Custom comparator function. Mutually exclusive with numericFields.
 * @param params.numericFields - Field names that should be compared numerically instead of lexically.
 *   Mutually exclusive with sortFunction.
 * @returns Sorted array of DTO.
 */
export const useSortedRows = <DTO extends Record<string, unknown>, F extends string = string>({
  rows,
  sortParameters,
  sortFunction,
  numericFields,
}: UseSortedRowsParams<DTO, F>): DTO[] => {
  const compare = sortFunction ?? compareRowsByFieldName;

  return useMemo(
    () =>
      [...rows].sort((rowA: DTO, rowB: DTO) => {
        if (!sortParameters.sort) {
          return 0;
        }
        const fieldName = sortParameters.sort as F;
        const result = numericFields?.includes(fieldName)
          ? compareTwoValues(toSortableNumber(rowA[fieldName]), toSortableNumber(rowB[fieldName]))
          : compare(rowA, rowB, fieldName);
        return sortParameters.order === OrderDirection.DESC ? -result : result;
      }),
    [sortParameters, compare, rows, numericFields],
  );
};
